import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Loader2, CheckCircle, Mail, Crown, ArrowRight, AlertTriangle, Gift, ClipboardList } from 'lucide-react';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '@/context/AuthContext';
import { VIPProfileModal, ConsultationRequestModal } from '@/components/VIPForms';
import API_BASE_URL from '@/utils/apiUrl';

const BACKEND_URL = API_BASE_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser, user, token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const [isDemoMode, setIsDemoMode] = useState(false);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [showConsultationForm, setShowConsultationForm] = useState(false);
  const [profileCompleted, setProfileCompleted] = useState(false);
  const maxAttempts = 10;
  const pollInterval = 2000;

  const sessionId = searchParams.get('session_id');
  const isDemo = searchParams.get('demo') === 'true';
  const planId = searchParams.get('plan') || paymentData?.planId || '';

  // PDF generation function
  const generatePdfDownload = async (userData, partnerInfo = null) => {
    try {
      const pythagorasSquare = JSON.parse(localStorage.getItem('pythagorasSquare') || '{}');
      const compatibilityData = JSON.parse(localStorage.getItem('compatibilityData') || '{}');

      const requestData = {
        userData: {
          firstName: userData.firstName || 'Vartotojas',
          lastName: userData.lastName || '',
          day: parseInt(userData.day) || 1,
          month: parseInt(userData.month) || 1,
          year: parseInt(userData.year) || 2000,
          lifePath: parseInt(userData.lifePath) || 1,
          expression: userData.expression ? parseInt(userData.expression) : null,
          soul: userData.soul ? parseInt(userData.soul) : null,
          personality: userData.personality ? parseInt(userData.personality) : null
        },
        pythagorasSquare: pythagorasSquare,
        partnerData: null,
        compatibility: null
      };

      // Add partner data if available
      if (partnerInfo && partnerInfo.firstName) {
        requestData.partnerData = {
          firstName: partnerInfo.firstName || '',
          lastName: partnerInfo.lastName || '',
          day: parseInt(partnerInfo.day) || 1,
          month: parseInt(partnerInfo.month) || 1,
          year: parseInt(partnerInfo.year) || 2000,
          lifePath: parseInt(partnerInfo.lifePath) || 1
        };
        requestData.compatibility = compatibilityData;
      }

      const response = await axios.post(
        `${BACKEND_URL}/api/`,
        requestData,
        { 
          responseType: 'blob',
          timeout: 30000
        }
      );

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      return url;
    } catch (error) {
      console.error('PDF generation error:', error);
      return null;
    }
  };

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkPaymentStatus = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/payments/checkout-status/${sessionId}`);
        const data = response.data;

        // Check for demo mode
        if (data.demo_mode || isDemo) {
          setIsDemoMode(true);
        }

        if (data.payment_status === 'paid' || data.demo_mode) {
          setStatus('success');
          setPaymentData(data);
          
          // Refresh user to update VIP status in auth context
          await refreshUser();
          
          // CRITICAL: Reset numerologyData to the authenticated user's data to avoid session mix-up
          // This ensures users see their own profile after payment, not someone else's from "Mano ratas"
          if (user) {
            const userData = {
              firstName: user.firstName,
              lastName: user.lastName,
              day: user.day,
              month: user.month,
              year: user.year,
              lifePath: user.lifePath,
              gender: user.gender || null
            };
            localStorage.setItem('numerologyData', JSON.stringify(userData));
          }
          
          // Trigger confetti celebration!
          confetti({
            particleCount: 150,
            spread: 80,
            origin: { y: 0.6 },
            colors: ['#d4af37', '#9370db', '#4169e1']
          });

          // PDF GENERATION TEMPORARILY DISABLED
          // PDF bus išsiųsta automatiškai el. paštu per artimiausias 2 savaites
          // Don't generate PDF - user will see info message instead
          setPdfUrl(null);
          
          return; // Stop polling
        } else if (data.status === 'expired') {
          setStatus('expired');
          return;
        } else {
          // Keep polling
          if (attempts < maxAttempts) {
            setAttempts(prev => prev + 1);
            setTimeout(checkPaymentStatus, pollInterval);
          } else {
            setStatus('timeout');
          }
        }
      } catch (error) {
        console.error('Payment status check error:', error);
        if (attempts < maxAttempts) {
          setAttempts(prev => prev + 1);
          setTimeout(checkPaymentStatus, pollInterval);
        } else {
          setStatus('error');
        }
      }
    };

    checkPaymentStatus();
  }, [sessionId, refreshUser, user]);

  const handleDownloadPdf = () => {
    if (pdfUrl) {
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `NUMENAXIS_Sielos_Knyga_${paymentData?.firstName || 'Vartotojas'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleGoToResults = () => {
    navigate('/rezultatai');
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gradient-to-br from-violet-950/90 via-indigo-950/90 to-slate-950/90 border-amber-500/30 p-8 text-center">
          <Loader2 className="w-16 h-16 text-amber-400 animate-spin mx-auto mb-6" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Tikrinamas mokėjimas...</h2>
          <p className="text-muted-foreground">Palaukite, kol patvirtinsime jūsų mokėjimą</p>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    // Determine if this is a premium plan (VIP+ with consultations)
    const isPremiumPlan = planId.includes('premium') || paymentData?.planId?.includes('premium');
    // Determine if this is a yearly plan (for PDF generation)
    const isYearlyPlan = planId.includes('yearly') || planId.includes('premium') || 
                         paymentData?.planId?.includes('yearly') || paymentData?.planId?.includes('premium');

    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full bg-gradient-to-br from-violet-950/90 via-indigo-950/90 to-slate-950/90 border-amber-500/50 p-8">
          {/* Demo Mode Warning */}
          {isDemoMode && (
            <div className="bg-yellow-500/20 border border-yellow-500/50 rounded-lg p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-200 font-medium">DEMO režimas</p>
                <p className="text-xs text-yellow-200/70">
                  Mokėjimo sistema veikia demonstraciniu režimu. VIP prieiga suteikta be tikro mokėjimo.
                </p>
              </div>
            </div>
          )}
          
          {/* Success Header */}
          <div className="text-center mb-8">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-green-500/50">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-3xl font-bold text-amber-400 mb-2">
              🎉 {isDemoMode ? 'VIP prieiga suteikta!' : 'Mokėjimas sėkmingas!'}
            </h1>
            <p className="text-foreground/80">
              Sveikiname, <span className="text-amber-400 font-semibold">{paymentData?.firstName || user?.firstName}</span>! 
              Tapote NUMENAXIS VIP nariu.
            </p>
          </div>

          {/* VIP Benefits - Compact */}
          <div className="bg-amber-500/10 rounded-lg p-4 mb-6 border border-amber-500/30">
            <div className="flex items-center gap-2 mb-3">
              <Crown className="w-5 h-5 text-amber-400" />
              <span className="font-semibold text-amber-400">Jūsų VIP prieiga aktyvuota!</span>
            </div>
            <p className="text-sm text-foreground/70">
              {isPremiumPlan 
                ? '✨ Pilna prieiga + 6 asmeninės konsultacijos'
                : isYearlyPlan 
                  ? '✨ Pilna prieiga + PDF Sielos Knyga + 12 mėn. žemėlapis'
                  : '✨ Pilna prieiga prie visų VIP funkcijų'}
            </p>
          </div>

          {/* Data Collection Section - Only for Yearly and Premium plans */}
          {isYearlyPlan && !profileCompleted && (
            <div className="bg-gradient-to-r from-purple-950/50 to-indigo-950/50 rounded-lg p-4 mb-6 border border-purple-500/30">
              <div className="flex items-start gap-3">
                <ClipboardList className="w-5 h-5 text-purple-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-purple-400 mb-1">
                    {isPremiumPlan ? 'Paskutinis žingsnis!' : 'Užbaikite profilį'}
                  </h3>
                  <p className="text-xs text-foreground/70 mb-3">
                    {isPremiumPlan 
                      ? 'Užpildykite formą, kad galėtume pasiruošti jūsų konsultacijoms.'
                      : 'Pateikite papildomus duomenis asmeninei PDF Sielos Knygai.'}
                  </p>
                  <Button
                    onClick={() => isPremiumPlan ? setShowConsultationForm(true) : setShowProfileForm(true)}
                    size="sm"
                    className="bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white"
                  >
                    <ClipboardList className="w-4 h-4 mr-2" />
                    {isPremiumPlan ? 'Užpildyti konsultacijos formą' : 'Užpildyti profilį'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Profile Completed Message */}
          {profileCompleted && (
            <div className="bg-green-500/10 rounded-lg p-4 mb-6 border border-green-500/30">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">
                  {isPremiumPlan ? 'Konsultacijos užklausa išsiųsta!' : 'Profilis užpildytas!'}
                </span>
              </div>
              <p className="text-xs text-foreground/70 mt-1">
                {isPremiumPlan 
                  ? 'Susisieksime su jumis per 24 valandas.'
                  : 'PDF Sielos Knyga bus išsiųsta per 2 savaites.'}
              </p>
            </div>
          )}

          {/* PDF Info - TEMPORARILY DISABLED */}
          {isYearlyPlan && (
            <div className="space-y-3 mb-6">
              <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/30">
                <div className="flex items-start gap-3">
                  <Gift className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-400 font-semibold mb-2">
                      🎁 PDF Sielos Knyga
                    </p>
                    <p className="text-xs text-foreground/80 leading-relaxed">
                      PDF bus išsiųsta automatiškai el. paštu per artimiausias 2 savaites.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2 text-sm text-muted-foreground justify-center">
                <Mail className="w-4 h-4" />
                <span>El. paštas: {paymentData?.email || user?.email}</span>
              </div>
            </div>
          )}

          {/* Go to Results */}
          <Button
            onClick={handleGoToResults}
            variant="outline"
            className="w-full border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
          >
            Peržiūrėti VIP turinį
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Card>

        {/* VIP Profile Modal (for Yearly plans) */}
        <VIPProfileModal
          isOpen={showProfileForm}
          onClose={() => setShowProfileForm(false)}
          onSuccess={() => setProfileCompleted(true)}
          user={user}
          token={token}
          isPremium={isPremiumPlan}
        />

        {/* Consultation Request Modal (for VIP+ Premium) */}
        <ConsultationRequestModal
          isOpen={showConsultationForm}
          onClose={() => setShowConsultationForm(false)}
          onSuccess={() => setProfileCompleted(true)}
          user={user}
          token={token}
        />
      </div>
    );
  }

  // Error/Timeout/Expired states
  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-950 flex items-center justify-center p-4">
      <Card className="max-w-md w-full bg-gradient-to-br from-violet-950/90 via-indigo-950/90 to-slate-950/90 border-red-500/30 p-8 text-center">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-4xl">😔</span>
        </div>
        <h2 className="text-xl font-semibold text-foreground mb-2">
          {status === 'expired' ? 'Sesija pasibaigė' : 
           status === 'timeout' ? 'Laukimo laikas baigėsi' : 
           'Kažkas nepavyko'}
        </h2>
        <p className="text-muted-foreground mb-6">
          {status === 'expired' ? 'Mokėjimo sesija pasibaigė. Bandykite dar kartą.' :
           status === 'timeout' ? 'Mokėjimo patikrinimas užtruko per ilgai. Jei sumokėjote, VIP prieiga bus suaktyvinta netrukus.' :
           'Įvyko klaida tikrinant mokėjimą. Bandykite dar kartą arba susisiekite su mumis.'}
        </p>
        <Button
          onClick={() => navigate('/rezultatai')}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black"
        >
          Grįžti atgal
        </Button>
      </Card>
    </div>
  );
}
